import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSuccessFailure } from '../context/SuccessFailureContext';
import axios from 'axios';
import config from '../config';
import { FiAlertTriangle, FiTrash2, FiX, FiCheck, FiClock } from 'react-icons/fi';
import ErrorBoundary from '../components/ErrorBoundary';
import NotificationSettings from '../components/NotificationSettings';
import ChangePasswordCard from '../components/ChangePasswordCard';

// Add debug logging
const debug = false;  // Debug logging - Disabled for production
const log = (...args) => {
  if (debug) {
    // console.log('[SettingsPage]', ...args);
  }
};

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const navigate = useNavigate();

  // State for account deletion
  const [deletionStatus, setDeletionStatus] = useState({
    hasPendingDeletion: false,
    isVerified: false,
    scheduledForDeletion: null,
    daysRemaining: null
  });

  // State for dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check deletion status on component mount
  useEffect(() => {
    log('Component mounted, checking auth and deletion status');
    log('Current user:', user);
    log('Is logged in:', !!user);

    if (user) {
      checkDeletionStatus().catch(err => {
        log('Error checking deletion status:', err);
        setError('Failed to load account status. Please refresh the page.');
      });
    } else {
      log('No user found, redirecting to login');
      navigate('/login', { state: { from: '/settings' } });
    }
  }, [user, navigate]);

  const checkDeletionStatus = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      log('Fetching deletion status with token:', token ? 'token exists' : 'no token');

      const response = await axios.get(
        `${config.API_BASE_URL}/users/account/delete/status/`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      // log('Deletion status response:', response.data);

      setDeletionStatus({
        hasPendingDeletion: response.data.has_pending_deletion || false,
        isVerified: response.data.is_verified || false,
        scheduledForDeletion: response.data.scheduled_for_deletion,
        daysRemaining: response.data.days_remaining
      });
    } catch (error) {
      // console.error('Error checking deletion status:', error);
      showFailureModal('Failed to check account deletion status. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setError('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.API_BASE_URL}/users/account/delete/request/`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setDeleteDialogOpen(false);
      setVerificationDialogOpen(true);
      showSuccessModal('Verification code sent to your email. Please check your inbox.');
    } catch (error) {
      // console.error('Error requesting account deletion:', error);
      setError(error.response?.data?.detail || 'Failed to request account deletion. Please try again.');
      showFailureModal('Failed to request account deletion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.API_BASE_URL}/users/account/delete/verify/`,
        { verification_code: verificationCode },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setVerificationDialogOpen(false);
      setVerificationCode('');
      await checkDeletionStatus();
      showSuccessModal('Your account has been scheduled for deletion. You can cancel this request until the scheduled date.');
    } catch (error) {
      // console.error('Error verifying code:', error);
      setError(error.response?.data?.detail || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.API_BASE_URL}/users/account/delete/cancel/`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await checkDeletionStatus();
      showSuccessModal('Account deletion has been cancelled successfully.');
    } catch (error) {
      // console.error('Error cancelling deletion:', error);
      showFailureModal('Failed to cancel account deletion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Add a loading state
  if (isLoading && !deletionStatus.hasOwnProperty('hasPendingDeletion')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Log the current state for debugging
  log('Rendering with state:', {
    user,
    deletionStatus,
    deleteDialogOpen,
    verificationDialogOpen,
    isLoading
  });

  return (
    <ErrorBoundary>
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>

        {/* Notification Preferences */}
        <NotificationSettings />

        {/* Change Password */}
        <ChangePasswordCard />

        {/* Account Deletion Section */}
        <div className="border border-red-200 rounded-lg overflow-hidden bg-red-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <FiAlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h2 className="text-xl font-semibold text-red-700">Danger Zone</h2>
            </div>

            {deletionStatus.hasPendingDeletion && deletionStatus.isVerified ? (
              <div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FiClock className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Your account is scheduled for deletion on <span className="font-medium">{formatDate(deletionStatus.scheduledForDeletion)}</span>.
                        {deletionStatus.daysRemaining > 0 && (
                          <span className="font-bold"> ({deletionStatus.daysRemaining} days remaining)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCancelDeletion}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <FiX className="-ml-1 mr-2 h-5 w-5" />
                  )}
                  Cancel Account Deletion
                </button>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={() => setDeleteDialogOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FiTrash2 className="-ml-1 mr-2 h-5 w-5" />
                  Delete My Account
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Delete Account Confirmation Dialog */}
        {deleteDialogOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <FiAlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Delete Your Account?
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete your account? This action cannot be undone.
                          All your data will be permanently deleted after a 30-day waiting period.
                          You'll receive a verification code to confirm this action.
                        </p>
                      </div>
                      {error && (
                        <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm rounded-md">
                          {error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : 'Delete My Account'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteDialogOpen(false)}
                    disabled={isLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Verification Code Dialog */}
        {verificationDialogOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <FiCheck className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Verify Account Deletion
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          We've sent a verification code to your email address.
                          Please enter the code below to confirm account deletion.
                        </p>
                        <div className="mt-4">
                          <input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                            placeholder="Enter 5-character code"
                            autoFocus
                          />
                        </div>
                        {error && (
                          <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm rounded-md">
                            {error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleVerifyCode}
                    disabled={isLoading || !verificationCode}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </>
                    ) : 'Verify & Delete Account'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setVerificationDialogOpen(false);
                      setError('');
                    }}
                    disabled={isLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default SettingsPage;


